import { Masonry } from '@mui/lab'
import { Box, Paper, Typography } from '@mui/material'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CancelRoundedIcon from '@mui/icons-material/CancelRounded'
import RemoveCircleRoundedIcon from '@mui/icons-material/RemoveCircleRounded'
import { useTranslation } from 'react-i18next'
import styles from './styles.module.scss'
import { RenderTooltip } from '../../../../../components/utilities/RenderTooltip'
import { useMemo } from 'react'
const getAnalyticsTranslation = (key, t) => {
  const translationMap = {
    mrzFormat: 'analyticsMrzFormat',
    mrzChecksum: 'analyticsMrzChecksum',
    lastName: 'lastName',
    firstName: 'firstName',
    dob: 'birthDate',
    documentNumber: 'documentNumber',
    personalNumber: 'personalNumber',
    expirationDate: 'expirationDate',
    issuingDate: 'analyticsIssuingDate',
    dataIntegrity: 'analyticsDataIntegrity',
    specimenCheck: 'analyticsSpecimenCheck',
    blackListCheck: 'analyticsBlackListCheck',
    modelIdentification: 'analyticsModelIdentification',
    countryModelValidity: 'analyticsCountryModelValidity',
    documentModelValidity: 'analyticsDocumentModelValidity',
    photocopyDetected: 'analyticsPhotocopyDetected',
    mrzVisualPlacement: 'analyticsMrzVisualPlacement',
    securityElements: 'analyticsSecurityElements',
    photoLocation: 'analyticsPhotoLocation',
    mrzClassification: 'analyticsMrzClassification',
    documentLivenessCheck: 'analyticsDocumentLivenessCheck',
    tamperingDetected: 'analyticsTamperingDetected',
    formatAnalysis: 'analyticsFormatAnalysis',
    dataConsistency: 'analyticsDataConsistency',
    ageVerification: 'analyticsAgeVerification',
    clientDataConsistency: 'analyticsClientDataConsistency',
    facialSimilarity: 'analyticsFacialSimilarity',
    previouslyEnrolledFace: 'analyticsPreviouslyEnrolledFace',
    spoofedImageAnalysis: 'analyticsSpoofedImageAnalysis',
    livenessCheck: 'analyticsLivenessCheck',
    faceDetection: 'analyticsFaceDetection',
    vpnDetected: 'analyticsVpnDetected',
    controlNumber: 'analyticsControlNumber',
    nationality: 'nationality',
    procedureNumber: 'procedureNumber',
  }
  const translationKey = translationMap[key]
  return translationKey ? t(`common:${translationKey}`) : key
}
const excludedCheckAnlitics = ['rfidAnalysis', 'securityAndPatternAnalysis', 'extractedImages', 'extractedData']
const CheckAnalytics = ({ data, renaperData, checkMetadata, issuingCountry }) => {
  const { t } = useTranslation(['common', 'messages'])
  const renaperValidation = checkMetadata?.renaperValidation
  const secondCheckData = checkMetadata?.secondCheckData
  const controlNumber = data?.result?.breakdown?.extractedData?.documentDetails?.personalNumber
  const renaper = useMemo(() => {
    // Si el país no es Argentina, no mostrar RENAPER
    if (issuingCountry !== 'AR') {
      return null
    }
    
    if (!renaperValidation || !renaperValidation.reason) {
      return null
    }
    const renaperAnalytics = {}
    
    if (renaperValidation.enabled) {
      if (renaperValidation.reason === 'procedureNumberMatches') {
        renaperAnalytics.procedureNumberMatches = 'clear'
      } else if (renaperValidation.reason === 'procedureNumberDoesNotMatch') {
        renaperAnalytics.procedureNumberDoesNotMatch = 'attention'
      } else if (renaperValidation.reason === 'complyCubeNotReadProcedureNumber') {
        renaperAnalytics.complyCubeNotReadProcedureNumber = 'attention'
      } else if (renaperValidation.reason === 'couldNotRetrieveRenaperData') {
        renaperAnalytics.couldNotRetrieveRenaperData = 'not_processed'
      }
      return Object.keys(renaperAnalytics).length > 0 ? renaperAnalytics : null

    } else if (renaperValidation.reason === 'renaperValidationNotEnabled') {
      renaperAnalytics.renaperValidationNotEnabled = 'not_processed'
      return renaperAnalytics
    }
    
    return null
  }, [controlNumber, renaperData, renaperValidation, issuingCountry])

  const secondCheck = useMemo(() => {
    if (!secondCheckData) {
      return null
    }
    const secondCheckAnalytics = {warnings: secondCheckData?.warnings?.map((warning) => warning.short_description)?.join('. ')}
    
    return Object.keys(secondCheckAnalytics).length > 0 ? secondCheckAnalytics : null
  }, [secondCheckData])

  const checkAnalytics = useMemo(() => {
    if (data?.result?.breakdown) {
      const resultData = {
        ...data.result.breakdown,
        ...(renaper && { renaper }),
        ...(secondCheck && { secondCheck }),
      }
      const filteredData = Object.keys(resultData)
        .filter((key) => !excludedCheckAnlitics.includes(key))
        .reduce((obj, key) => {
          obj[key] = resultData[key]
          return obj
        }, {})
      const analytics = Object.entries(filteredData).reduce((acc, [key, value]) => {
        const newValue = { ...value }
        if (newValue.breakdown) {
          Object.assign(acc, newValue.breakdown)
          delete newValue.breakdown
        }
        acc[key] = newValue
        return acc
      }, {})
      return analytics
    }
    return null
  }, [data, renaper])

  if (!checkAnalytics) return null
  
  const getRenaperCriterionText = (key) => {
    const translationKey = `messages:kyc.check.analytics.renaper.${key}`
    const translated = t(translationKey)
    return translated
  }
  
  return (
    <Box>
      <Paper
        elevation={0}
        variant="colorPrimary"
        className={styles.onboardingAnalysis}
        style={{ marginBottom: '16px' }}
      >
        <Box style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="subtitle1">{t('common:analyticsLegend')}:</Typography>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CheckCircleRoundedIcon fontSize="small" color="secondary" />
            <Typography variant="body2">{t('common:analyticsLegendGood')}</Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <RemoveCircleRoundedIcon fontSize="small" className={styles.grey} />
            <Typography variant="body2">{t('common:analyticsLegendNotVerified')}</Typography>
          </Box>
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CancelRoundedIcon fontSize="small" className={styles.red} />
            <Typography variant="body2">{t('common:analyticsLegendHighRisk')}</Typography>
          </Box>
        </Box>
      </Paper>
      <Masonry columns={{ xs: 1, sm: 2, md: 2 }} spacing={2}>
        {Object.keys(checkAnalytics).map((item) => (
          <Paper elevation={0} variant="colorPrimary" className={styles.onboardingAnalysis} key={item}>
            <Box className={styles.onboardingTooltip}>
              <Typography variant="subtitle1">{t(`messages:kyc.check.analytics.${item}`)}</Typography>
              <RenderTooltip title={t(`messages:kyc.check.analytics.${item}.tooltip`)} />
            </Box>
            {typeof checkAnalytics[item] === 'object' &&
              Object.entries(checkAnalytics[item]).map(([key, value]) => (
                <Box className={styles.checkRender} key={key}>
                  {(!value || value === 'not_processed') && (
                    <RemoveCircleRoundedIcon fontSize="small" className={styles.grey} />
                  )}
                  {value === 'clear' && <CheckCircleRoundedIcon fontSize="small" color="secondary" />}
                  {value && value !== 'clear' && value !== 'not_processed' && (
                    <CancelRoundedIcon fontSize="small" className={styles.red} />
                  )}
                  <p>{item === 'renaper' ? getRenaperCriterionText(key) : item === "secondCheck" ? value : getAnalyticsTranslation(key, t)}</p>
                </Box>
              ))}
            {typeof checkAnalytics[item] === 'number' && (
              <Typography variant="number">{checkAnalytics[item]}%</Typography>
            )}
          </Paper>
        ))}
      </Masonry>
      {renaper?.procedureNumberDoesNotMatch && (
        <Box mt={2}>
          <Paper
            elevation={0}
            variant="colorPrimary"
            className={styles.onboardingAnalysis}
            style={{
              border: '2px solid #f44336',
              backgroundColor: '#ffebee',
            }}
          >
            <Typography
              variant="body1"
              style={{
                color: '#d32f2f',
                fontWeight: 'bold',
                textAlign: 'center',
                padding: '8px',
              }}
            >
              {t('messages:kyc.check.analytics.renaper.rejectionWarning')}
            </Typography>
          </Paper>
        </Box>
      )}
    </Box>
  )
}
export default CheckAnalytics